import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Search, Crown, X, Trophy, Medal, Star, Share2, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";

const EASE = [0.22, 1, 0.36, 1];
const SEASONS = [1, 2, 3, 4, 5];

const AWARDS = {
  "Aman Prajapati": [{ label: "Orange Cap S5.0", color: "#ea580c" }, { label: "Most 6s S5.0", color: "#9f1239" }],
  "Kanak Beewal": [{ label: "Player of the Series S2/S4/S5", color: "#b45309" }],
  "Mitesh Gupta": [{ label: "Purple Cap S5.0", color: "#6d28d9" }],
  "Lokesh Jangir": [{ label: "Most 4s S5.0", color: "#be185d" }],
  "Saurabh Agarwal": [{ label: "Most Catches S5.0", color: "#4338ca" }],
  "Neet Savaliya": [{ label: "Most Dots Bowled S5.0", color: "#b91c1c" }],
  "Pankaj Sharma": [{ label: "Player of the Series S1.0", color: "#b45309" }],
  "Kush Jariwala": [{ label: "Player of the Series S3.0", color: "#b45309" }],
};

function PlayerModal({ player, winners, onClose }) {
  const [career, setCareer] = useState(null);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    api.get(`/stats/career/${encodeURIComponent(player.name)}`)
      .then((r) => setCareer(r.data))
      .catch(() => setCareer(null));
  }, [player.name]);

  const honours = player.seasons.map((s) => {
    const w = winners.find((x) => x.season === s.season);
    if (!w) return { ...s, tag: null };
    if (w.champion === s.team) return { ...s, tag: "champion" };
    if (w.runner_up === s.team) return { ...s, tag: "runner" };
    return { ...s, tag: null };
  });
  const titles = honours.filter((h) => h.tag === "champion").length;
  const runnerUps = honours.filter((h) => h.tag === "runner").length;
  const awards = AWARDS[player.name] || [];

  const shareCard = async () => {
    if (!cardRef.current || sharing) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `${player.name.replace(/\s+/g, "-")}-ASL-stat-card.png`, { type: "image/png" });
      const text = `${player.name} — All Star League career card | ${career?.totals?.runs ?? 0} runs, ${career?.totals?.wickets ?? 0} wickets across S1.0–S5.0`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "All Star League player card", text });
        toast.success("Stat card shared");
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = file.name;
        a.click();
        toast.success("Stat card downloaded — share it on WhatsApp or Instagram");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the card — try again");
    } finally {
      setSharing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-league-ink/90 backdrop-blur-sm flex items-center justify-center p-5"
      onClick={onClose}
      data-testid="player-modal"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.3, ease: EASE }}
        className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-7 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="player-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-league-mist text-league-ink flex items-center justify-center transition-colors duration-200 hover:bg-league-red hover:text-white"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <span className="w-12 h-12 rounded-full bg-league-blue text-white flex items-center justify-center font-oswald text-xl font-semibold shrink-0">
            {player.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
          </span>
          <div>
            <h3 className="font-oswald text-2xl font-semibold text-league-ink leading-tight" data-testid="player-modal-name">{player.name}</h3>
            <p className="text-xs font-bold uppercase tracking-widest text-league-blue mt-0.5">
              {player.season_count} season{player.season_count > 1 ? "s" : ""} • Latest: {player.latest_team}
            </p>
          </div>
        </div>

        {(titles > 0 || runnerUps > 0) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {titles > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-600 text-[10px] font-bold uppercase tracking-widest" data-testid="player-modal-titles">
                <Trophy size={11} /> Champion ×{titles}
              </span>
            )}
            {runnerUps > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-league-mist border border-black/10 text-league-ink/60 text-[10px] font-bold uppercase tracking-widest" data-testid="player-modal-runners">
                <Medal size={11} /> Runner-up ×{runnerUps}
              </span>
            )}
          </div>
        )}

        {awards.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="player-modal-awards">
            {awards.map((a) => (
              <span key={a.label} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-[10px] font-bold uppercase tracking-widest" style={{ backgroundColor: a.color }}>
                <Star size={10} /> {a.label}
              </span>
            ))}
          </div>
        )}

        {career && (
          <div className="mt-6 border-t border-black/5 pt-5" data-testid="player-modal-career">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3">Career stats (S1.0–S5.0)</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                ["Runs", career.totals.runs],
                ["Wkts", career.totals.wickets],
                ["Dismissals", career.totals.dismissals],
                ["MVP pts", career.totals.mvp_points],
              ].map(([label, val]) => (
                <div key={label} className="rounded-lg bg-league-mist px-2 py-3 text-center">
                  <p className="font-oswald text-lg font-semibold text-league-blue leading-none">{val}</p>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-league-ink/50 mt-1.5">{label}</p>
                </div>
              ))}
            </div>
            <ul className="mt-3 space-y-1.5" data-testid="player-modal-career-seasons">
              {Object.keys(career.seasons).sort().map((s) => {
                const c = career.seasons[s];
                const bits = [];
                if (c.batting?.runs) bits.push(`${c.batting.runs} runs`);
                if (c.bowling?.wickets) bits.push(`${c.bowling.wickets} wkts`);
                if (c.fielding?.dismissals) bits.push(`${c.fielding.dismissals} dis`);
                if (c.mvp?.total) bits.push(`${c.mvp.total} MVP`);
                if (!bits.length) return null;
                return (
                  <li key={s} className="flex items-center gap-3 text-xs">
                    <span className="w-12 shrink-0 text-center py-0.5 rounded-md bg-league-blue/10 text-league-blue text-[10px] font-bold uppercase tracking-wider">S{s}.0</span>
                    <span className="text-league-ink/70 font-medium">{bits.join(" • ")}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-6 border-t border-black/5 pt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3">Season journey</p>
          <ul className="space-y-2.5" data-testid="player-modal-journey">
            {honours.map((h) => (
              <li key={h.season} className="flex items-center gap-3">
                <span className={`w-12 shrink-0 text-center py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${h.tag === "champion" ? "bg-amber-400 text-league-ink" : h.tag === "runner" ? "bg-league-ink/70 text-white" : "bg-league-mist text-league-ink/50"}`}>
                  S{h.season}.0
                </span>
                <span className="font-oswald text-sm font-semibold text-league-ink">{h.team}</span>
                {h.tag === "champion" && <Trophy size={14} className="text-amber-500 shrink-0" />}
                {h.tag === "runner" && <Medal size={14} className="text-league-ink/40 shrink-0" />}
              </li>
            ))}
          </ul>
        </div>

        {career && (
          <button
            onClick={shareCard}
            disabled={sharing}
            data-testid="player-modal-share-card"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-league-blue text-white text-xs font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-league-deep disabled:opacity-60 cursor-pointer"
          >
            {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />}
            {sharing ? "Creating card…" : "Share stat card"}
          </button>
        )}
      </motion.div>

      {career && (
        <div style={{ position: "fixed", left: "-2000px", top: 0, pointerEvents: "none" }} aria-hidden="true">
          <StatCard ref={cardRef} player={player} career={career} titles={titles} runnerUps={runnerUps} awards={awards} />
        </div>
      )}
    </motion.div>
  );
}

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [winners, setWinners] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [seasonFilter, setSeasonFilter] = useState(0);

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
    api.get("/winners").then((r) => setWinners(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return players.filter((p) => {
      const q = query.trim().toLowerCase();
      if (q && !p.name.toLowerCase().includes(q) && !p.latest_team.toLowerCase().includes(q)) return false;
      if (seasonFilter && !p.seasons.some((s) => s.season === seasonFilter)) return false;
      return true;
    });
  }, [players, query, seasonFilter]);

  const veterans = players.filter((p) => p.season_count === 5).length;

  return (
    <main className="pt-28 pb-24 bg-league-mist min-h-screen" data-testid="players-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue flex items-center gap-2">
          <Users size={14} /> Seasons 1.0 — 5.0
        </span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-league-ink tracking-tighter mt-3 mb-4">
          LEAGUE <span className="text-outline">PLAYERS</span>
        </h1>
        <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">
          Every player who has taken the field across five seasons of All Star League — {players.length} names and counting.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-league-ink/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a player or team…"
              data-testid="players-search"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-black/10 bg-white text-sm text-league-ink placeholder:text-league-ink/40 focus:outline-none focus:ring-2 focus:ring-league-blue"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="players-season-filters">
            <button
              onClick={() => setSeasonFilter(0)}
              data-testid="filter-season-all"
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${seasonFilter === 0 ? "bg-league-blue text-white" : "bg-white text-league-ink border border-black/10 hover:border-league-blue"}`}
            >
              All
            </button>
            {SEASONS.map((s) => (
              <button
                key={s}
                onClick={() => setSeasonFilter(seasonFilter === s ? 0 : s)}
                data-testid={`filter-season-${s}`}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${seasonFilter === s ? "bg-league-blue text-white" : "bg-white text-league-ink border border-black/10 hover:border-league-blue"}`}
              >
                S{s}.0
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-6" data-testid="players-count">
          {filtered.length} players {seasonFilter ? `in Season ${seasonFilter}.0` : "all-time"} {veterans > 0 && !seasonFilter && !query ? `• ${veterans} five-season veterans` : ""}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20px" }}
              transition={{ duration: 0.45, delay: (i % 6) * 0.05, ease: EASE }}
              onClick={() => setActive(p)}
              className="bg-white border border-black/5 rounded-xl p-5 text-left hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-14px_rgba(0,74,173,0.25)] transition-[transform,box-shadow] duration-300 cursor-pointer"
              data-testid={`player-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-oswald text-lg font-semibold text-league-ink leading-tight flex items-center gap-2">
                    {p.name}
                    {p.season_count === 5 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-600 text-[9px] font-bold uppercase tracking-widest shrink-0" title="Played all 5 seasons">
                        <Crown size={10} /> 5×
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Latest — {p.latest_team} (S{p.latest_season}.0)</p>
                </div>
                <span className="font-oswald text-2xl font-semibold text-league-blue shrink-0">{p.season_count}</span>
              </div>
              <div className="mt-4 flex gap-1.5" data-testid={`player-seasons-${p.id}`}>
                {SEASONS.map((s) => {
                  const played = p.seasons.find((x) => x.season === s);
                  return (
                    <span
                      key={s}
                      title={played ? `S${s}.0 — ${played.team}` : `S${s}.0 — didn't play`}
                      className={`flex-1 text-center py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${played ? "bg-league-blue text-white" : "bg-league-mist text-league-ink/25"}`}
                    >
                      S{s}
                    </span>
                  );
                })}
              </div>
            </motion.button>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-muted-foreground py-12 text-center" data-testid="players-empty">No players match that search.</p>
        )}
      </div>

      <AnimatePresence>
        {active && <PlayerModal player={active} winners={winners} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </main>
  );
}
